import React from 'react';
import { useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { ClickableIcon } from '~components';
import { faArrowRotateBack, faCheck, faEyeSlash, faLink, faLinkSlash } from '~components/icon';

import { CareRecipient } from '~support-at-home/models/careRecipients';

import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  row: CareRecipient;
  onLink?: (careRecipient: CareRecipient) => void;
  onIgnore?: (careRecipient: CareRecipient) => void;
  onRestore?: (careRecipient: CareRecipient) => void;
  onUnlink?: (careRecipient: CareRecipient) => void;
  isBusy?: boolean;
}

const CareRecipientActions: React.FC<ComponentProps> = ({
  row,
  onLink,
  onIgnore,
  onRestore,
  onUnlink,
  isBusy = false,
}) => {
  const userTags = useAppSelector(selectUserTags);

  // Linking writes the client's Medicare Ref., so every action needs the client edit permission.
  if (!userTags.includes(AuthTags.CLIENTS.UPDATE)) return null;

  const { id, match_status: status } = row;

  if (status === 'ignored') {
    return (
      <ClickableIcon
        id={`sah-care-recipient-restore-${id}`}
        dataCy={`sah-care-recipient-restore-${id}`}
        icon={faArrowRotateBack}
        color="success"
        toolTip="Restore"
        ariaLabel="Restore"
        disabled={isBusy}
        onClick={() => onRestore?.(row)}
      />
    );
  }

  if (status === 'matched') {
    return (
      <ClickableIcon
        id={`sah-care-recipient-unlink-${id}`}
        dataCy={`sah-care-recipient-unlink-${id}`}
        icon={faLinkSlash}
        color="danger"
        toolTip="Unlink"
        ariaLabel="Unlink"
        disabled={isBusy}
        onClick={() => onUnlink?.(row)}
      />
    );
  }

  if (status === 'conflict') {
    return (
      <span title="Correct the clients first" data-cy={`sah-care-recipient-conflict-note-${id}`}>
        -
      </span>
    );
  }

  const isSuggested = status === 'suggested';

  return (
    <div className="d-flex gap-2 justify-content-end align-items-center">
      <ClickableIcon
        id={`sah-care-recipient-${isSuggested ? 'accept' : 'link'}-${id}`}
        dataCy={`sah-care-recipient-${isSuggested ? 'accept' : 'link'}-${id}`}
        icon={isSuggested ? faCheck : faLink}
        color="success"
        toolTip={isSuggested ? 'Accept suggestion' : 'Link'}
        ariaLabel={isSuggested ? 'Accept suggestion' : 'Link'}
        disabled={isBusy}
        onClick={() => onLink?.(row)}
      />
      <ClickableIcon
        id={`sah-care-recipient-ignore-${id}`}
        dataCy={`sah-care-recipient-ignore-${id}`}
        icon={faEyeSlash}
        color="secondary"
        toolTip="Ignore"
        ariaLabel="Ignore"
        disabled={isBusy}
        onClick={() => onIgnore?.(row)}
      />
    </div>
  );
};

export default CareRecipientActions;
